import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { routes } from 'constants/routes';
import { Spinner } from '@ag1/ui';

import { useSessionValue } from './useSession';
import { useNavigateToReturnUrl } from './useNavigateToReturnUrl';
import { useSessionChangeHandler } from './useSessionChangeHandler';

interface Props {
  children: React.ReactNode;
}

const publicRoutes = [routes.login(), routes.activate(), routes.reset()];

const isPrivateRoute = (pathname: string) =>
  !publicRoutes.includes(pathname) && pathname.startsWith('/members');

export const AuthenticationGuard = ({ children }: Props) => {
  useSessionChangeHandler();

  const router = useRouter();
  const actualUrl = router.asPath.endsWith('#') ? router.asPath.slice(0, -1) : router.asPath;
  const queryParams = new URLSearchParams(router.query as Record<string, string>).toString();
  const actualUrlWithParams = queryParams ? `${actualUrl}?${queryParams}` : actualUrl;
  const session = useSessionValue();
  const navigateToReturnUrl = useNavigateToReturnUrl();

  useEffect(() => {
    if (session && router.pathname === routes.login()) {
      navigateToReturnUrl();
    }
  }, [navigateToReturnUrl, router.pathname, session]);

  useEffect(() => {
    if (!session && isPrivateRoute(router.pathname)) {
      const isSubscriptionDetailsPage =
        actualUrl.includes('subscriptions') && actualUrl.search(/\d/);
      const loginRoute = routes.login(
        isSubscriptionDetailsPage ? `${routes.home}` : actualUrlWithParams
      );
      const loginRouteWithParams = `${loginRoute}${
        loginRoute.includes('?') ? '&' : '?'
      }${queryParams}`;
      router.push(loginRouteWithParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname, actualUrl, session]);

  if (
    (!session && isPrivateRoute(router.pathname)) ||
    (session && router.pathname === routes.login())
  ) {
    return <Spinner blur />;
  }

  return <>{children}</>;
};
